import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useAllCohortsWithStats,
  useCreateCohort,
  useUpdateCohort,
  useDeleteCohort,
  useRefreshCohorts,
} from '@/hooks/useCohorts';
import { useToast } from '@/hooks/use-toast';
import { Plus, Loader2, Pencil, Trash2, MapPin, RefreshCw, Users, MessageSquare } from 'lucide-react';

export default function AdminCohorts() {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingCohort, setEditingCohort] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    city: '',
    is_active: true,
  });

  // Delete confirmation for a single cohort
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  // Empty cohorts confirmation after refresh
  const [emptyCohorts, setEmptyCohorts] = useState<{ id: string; name: string }[]>([]);
  const [showEmptyConfirm, setShowEmptyConfirm] = useState(false);

  const { data: cohorts, isLoading } = useAllCohortsWithStats();
  const createCohort = useCreateCohort();
  const updateCohort = useUpdateCohort();
  const deleteCohort = useDeleteCohort();
  const refreshCohorts = useRefreshCohorts();
  const { toast } = useToast();

  const handleRefreshCohorts = async () => {
    try {
      const result = await refreshCohorts.mutateAsync();
      toast({
        title: 'Cohorts refreshed',
        description: `${result.cohortsCreated} created, ${result.membersAssigned} members assigned, ${result.channelsCreated} channels created.`,
      });

      // If there are empty cohorts, show confirmation dialog
      if (result.emptyCohorts.length > 0) {
        setEmptyCohorts(result.emptyCohorts);
        setShowEmptyConfirm(true);
      }
    } catch (error: any) {
      toast({
        title: 'Error refreshing cohorts',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleDeleteEmptyCohorts = async () => {
    let deleted = 0;
    for (const cohort of emptyCohorts) {
      try {
        await deleteCohort.mutateAsync(cohort.id);
        deleted++;
      } catch (error: any) {
        toast({
          title: `Error deleting ${cohort.name}`,
          description: error.message,
          variant: 'destructive',
        });
      }
    }
    setShowEmptyConfirm(false);
    setEmptyCohorts([]);
    toast({
      title: 'Empty cohorts deleted',
      description: `${deleted} cohort(s) removed.`,
    });
  };

  const handleDeleteSingle = async () => {
    if (!deleteTarget) return;
    try {
      await deleteCohort.mutateAsync(deleteTarget.id);
      toast({
        title: 'Cohort deleted',
        description: `${deleteTarget.name} has been deleted.`,
      });
    } catch (error: any) {
      toast({
        title: 'Error deleting cohort',
        description: error.message,
        variant: 'destructive',
      });
    }
    setDeleteTarget(null);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      city: '',
      is_active: true,
    });
    setEditingCohort(null);
  };

  const handleSubmit = async () => {
    try {
      if (editingCohort) {
        await updateCohort.mutateAsync({
          id: editingCohort.id,
          ...formData,
        });
        toast({
          title: 'Cohort updated',
          description: `${formData.name} has been updated.`,
        });
      } else {
        await createCohort.mutateAsync(formData);
        toast({
          title: 'Cohort created',
          description: `${formData.name} has been created.`,
        });
      }
      setIsCreateOpen(false);
      resetForm();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const openEditDialog = (cohort: any) => {
    setEditingCohort(cohort);
    setFormData({
      name: cohort.name,
      slug: cohort.slug,
      description: cohort.description || '',
      city: cohort.city || '',
      is_active: cohort.is_active,
    });
    setIsCreateOpen(true);
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground">Cohorts</h1>
          <p className="text-muted-foreground text-sm">
            Manage city-based community cohorts
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleRefreshCohorts}
            disabled={refreshCohorts.isPending}
          >
            {refreshCohorts.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh Cohorts
          </Button>
        <Dialog open={isCreateOpen} onOpenChange={(open) => {
          setIsCreateOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Cohort
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingCohort ? 'Edit Cohort' : 'Create New Cohort'}
              </DialogTitle>
              <DialogDescription>
                {editingCohort
                  ? 'Update the cohort details below.'
                  : 'Add a new city cohort for your community.'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  placeholder="e.g., Sydney"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      name: e.target.value,
                      slug: editingCohort ? formData.slug : generateSlug(e.target.value),
                    });
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="slug">Slug</Label>
                <Input
                  id="slug"
                  placeholder="e.g., sydney"
                  value={formData.slug}
                  onChange={(e) =>
                    setFormData({ ...formData, slug: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  placeholder="City name (optional)"
                  value={formData.city}
                  onChange={(e) =>
                    setFormData({ ...formData, city: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Brief description of this cohort..."
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Active</Label>
                  <p className="text-sm text-muted-foreground">
                    Inactive cohorts are hidden from users
                  </p>
                </div>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, is_active: checked })
                  }
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsCreateOpen(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={
                  !formData.name ||
                  !formData.slug ||
                  createCohort.isPending ||
                  updateCohort.isPending
                }
              >
                {createCohort.isPending || updateCohort.isPending
                  ? 'Saving...'
                  : editingCohort
                  ? 'Update'
                  : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Cohorts</CardTitle>
          <CardDescription>
            {cohorts?.length || 0} cohorts configured
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>City</TableHead>
                  <TableHead>Members</TableHead>
                  <TableHead>Posts</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cohorts?.map((cohort) => (
                  <TableRow key={cohort.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{cohort.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>{cohort.city || '-'}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <Users className="h-3.5 w-3.5 text-muted-foreground" />
                        {cohort.memberCount}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <MessageSquare className="h-3.5 w-3.5 text-muted-foreground" />
                        {cohort.postCount}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          cohort.is_active
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                        }`}
                      >
                        {cohort.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(cohort)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => setDeleteTarget({ id: cohort.id, name: cohort.name })}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Single cohort delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the cohort, its memberships, and any associated channel.
              Members will not be deleted but will lose their cohort assignment.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSingle}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteCohort.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Empty cohorts cleanup confirmation (after refresh) */}
      <AlertDialog open={showEmptyConfirm} onOpenChange={setShowEmptyConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {emptyCohorts.length} empty cohort{emptyCohorts.length !== 1 ? 's' : ''} found
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p className="mb-3">
                  The following cohorts have zero members. Would you like to delete them?
                </p>
                <ul className="space-y-1">
                  {emptyCohorts.map((c) => (
                    <li key={c.id} className="flex items-center gap-2 text-sm">
                      <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                      {c.name}
                    </li>
                  ))}
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep All</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteEmptyCohorts}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteCohort.isPending ? 'Deleting...' : `Delete ${emptyCohorts.length} Empty Cohort${emptyCohorts.length !== 1 ? 's' : ''}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
